/**
 * BetaBroadcastCard.jsx — v1.1.0 (always-visible CTA)
 *
 * Mounted globally. Polls /api/beta/my-status on mount. If the user has
 * pending broadcasts, shows them one at a time as a dismissible card.
 * One-time → dismissed forever. Persistent → reappears until read.
 *
 * v1.1.0 — Restructured to a flex column with a SCROLLABLE body and a
 * STICKY footer holding the "Got it" button. Previously, long messages
 * (e.g., the 1059-char founder welcome) pushed the dismiss button below
 * the viewport on short mobile screens with no way to scroll, trapping
 * users behind the modal. Now max-h-[90vh] + footer pinning guarantees
 * the CTA is always tappable.
 */
import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Sparkles } from "lucide-react";
import { API, axios } from "@/api";
import { useAuth } from "@/contexts/AuthContext";

export default function BetaBroadcastCard() {
  const { user } = useAuth();
  const [queue, setQueue] = useState([]);
  const [current, setCurrent] = useState(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/beta/my-status`);
        if (cancelled) return;
        if (res.data?.is_beta_tester && Array.isArray(res.data.pending_broadcasts) && res.data.pending_broadcasts.length > 0) {
          setQueue(res.data.pending_broadcasts);
          setTimeout(() => setCurrent(res.data.pending_broadcasts[0]), 900);
        }
      } catch { /* offline / unauthorized — leave modal closed */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  const handleDismiss = async () => {
    if (!current || dismissing) return;
    setDismissing(true);
    try {
      await axios.post(`${API}/beta/broadcasts/${current.broadcast_id}/dismiss`);
    } catch { /* swallow — still dismiss locally so user is never trapped */ }
    const rest = queue.slice(1);
    setQueue(rest);
    setCurrent(rest[0] || null);
    setDismissing(false);
  };

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9997] flex items-center justify-center p-4"
          data-testid="beta-broadcast-card"
        >
          <div className="absolute inset-0 bg-black/50" onClick={handleDismiss} />
          <motion.div
            initial={{ y: 12, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="relative bg-white rounded-3xl max-w-sm w-full max-h-[90vh] flex flex-col shadow-2xl"
          >
            <button
              onClick={handleDismiss}
              disabled={dismissing}
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200 transition z-10 disabled:opacity-50"
              data-testid="beta-broadcast-dismiss"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>
            {/* Scrollable body so any long message stays readable AND
                the footer button always remains in view. */}
            <div className="px-6 pt-6 pb-3 overflow-y-auto flex-1">
              <div className="flex items-center gap-1.5 mb-2">
                <Sparkles className="w-3.5 h-3.5 text-amber-500" />
                <span className="text-[10px] font-black tracking-widest uppercase text-amber-600">
                  From the Founder
                </span>
              </div>
              <h3 className="text-base font-bold text-slate-800 pr-8" data-testid="beta-broadcast-subject">
                {current.subject}
              </h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed whitespace-pre-wrap" data-testid="beta-broadcast-body">
                {current.body}
              </p>
            </div>
            {/* Sticky footer — the Got-it CTA is ALWAYS visible. */}
            <div className="px-6 py-4 border-t border-slate-100 bg-white rounded-b-3xl">
              <button
                onClick={handleDismiss}
                disabled={dismissing}
                className="w-full py-3 rounded-xl bg-slate-800 text-white text-sm font-bold hover:bg-slate-900 transition disabled:opacity-60"
                data-testid="beta-broadcast-ack"
              >
                {dismissing ? "Closing…" : "Got it — continue"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
